import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import { formatarCNPJ } from '../utils/formatters';
import '../styles/dctfweb.css';

const Dctfweb = () => {
  const hoje = new Date();
  const mesAtual = String(hoje.getMonth() + 1).padStart(2, '0');
  const anoAtual = hoje.getFullYear();
  const [competencia, setCompetencia] = useState(`${mesAtual}/${anoAtual}`);
  const [fonte, setFonte] = useState('dp'); // 'dp' = Departamento Pessoal | 'fiscal' = Relatório Fiscal

  const [empresas, setEmpresas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [executando, setExecutando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Estado para o modal de cadastro de empresa
  const [showModal, setShowModal] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState({
    cod: '',
    razaosocial: '',
    operador: '',
    cnpj: ''
  });
  const [cadastrando, setCadastrando] = useState(false);

  // Estado para o modal de edição de empresa
  const [showEditModal, setShowEditModal] = useState(false);
  const [empresaEditando, setEmpresaEditando] = useState({
    cnpjOriginal: '',
    cod: '',
    razaosocial: '',
    operador: '',
    cnpj: ''
  });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const [filtros, setFiltros] = useState({
    cod: '',
    razaosocial: '',
    operador: '',
    cnpj: '',
    data_vencimento: '',
    valor: '',
    data_geracao: '',
  });

  const carregarEmpresas = useCallback(() => {
    const token = localStorage.getItem('access');
    const url = fonte === 'fiscal'
      ? `${process.env.REACT_APP_API_BASE}/api/empresas-das/`
      : `${process.env.REACT_APP_API_BASE}/api/empresas-dctfweb/?competencia=${competencia}`;

    axios
      .get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setEmpresas(res.data);
      })
      .catch((error) => {
        console.error('Erro ao carregar empresas:', error);
        setMensagem('Erro ao carregar empresas.');
      });
  }, [competencia, fonte]);

  useEffect(() => {
    if (fonte === 'dp' && !competencia) return;
    carregarEmpresas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competencia, fonte]);

  // Efeito para definir o estilo do body quando algum modal estiver aberto
  useEffect(() => {
    if (showModal || showEditModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup function
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showModal, showEditModal]);

  const handleCheckbox = (cnpj) => {
    setSelecionadas((prev) =>
      prev.includes(cnpj) ? prev.filter((item) => item !== cnpj) : [...prev, cnpj]
    );
  };

  // Funções para o modal de cadastro
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNovaEmpresa(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limparFormulario = () => {
    setNovaEmpresa({
      cod: '',
      razaosocial: '',
      operador: '',
      cnpj: ''
    });
  };

  const abrirEdicao = (empresa) => {
    setEmpresaEditando({
      cnpjOriginal: empresa.cnpj,
      cod: empresa.cod || '',
      razaosocial: empresa.razaosocial || '',
      operador: empresa.operador || '',
      cnpj: empresa.cnpj || ''
    });
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEmpresaEditando(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const cadastrarEmpresa = async () => {
    console.log('Cadastrando nova empresa:', novaEmpresa);
    
    // Validação básica
    if (!novaEmpresa.cod || !novaEmpresa.razaosocial || !novaEmpresa.cnpj) {
      setMensagem('Preencha todos os campos obrigatórios: Código, Razão Social e CNPJ');
      return;
    }

    // Limpar o CNPJ para conter apenas números
    const cnpjLimpo = novaEmpresa.cnpj.replace(/\D/g, '');
    
    // Verificar se o CNPJ tem exatamente 14 dígitos
    if (cnpjLimpo.length !== 14) {
      setMensagem('CNPJ deve conter exatamente 14 dígitos');
      return;
    }

    setCadastrando(true);
    setMensagem('Cadastrando empresa...');

    try {
      // Criar objeto com CNPJ apenas com números
      const empresaParaEnviar = {
        ...novaEmpresa,
        cnpj: cnpjLimpo // Enviar apenas os números do CNPJ
      };
      
      const token = localStorage.getItem('access');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/cadastrar-empresa/`,
        empresaParaEnviar,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Resposta do servidor:', response.data);
      setMensagem('Empresa cadastrada com sucesso!');
      setShowModal(false);
      limparFormulario();
      
      // Recarregar a lista de empresas
      carregarEmpresas();
    } catch (error) {
      console.error('Erro ao cadastrar empresa:', error);
      setMensagem(`Erro ao cadastrar empresa: ${error.response?.data?.mensagem || error.message}`);
    } finally {
      setCadastrando(false);
    }
  };

  const salvarEdicao = async () => {
    if (fonte === 'dp' && (!empresaEditando.cod || !empresaEditando.razaosocial || !empresaEditando.cnpj)) {
      setMensagem('Preencha todos os campos obrigatórios: Código, Razão Social e CNPJ');
      return;
    }
    if (fonte === 'fiscal' && (!empresaEditando.razaosocial || !empresaEditando.cnpj)) {
      setMensagem('Preencha todos os campos obrigatórios: Razão Social e CNPJ');
      return;
    }

    const cnpjLimpo = empresaEditando.cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      setMensagem('CNPJ deve conter exatamente 14 dígitos');
      return;
    }

    setSalvandoEdicao(true);
    setMensagem('Salvando alterações...');

    try {
      const token = localStorage.getItem('access');

      if (fonte === 'dp') {
        await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/atualizar-empresa-dctfweb/`,
          {
            cnpj_original: empresaEditando.cnpjOriginal,
            cod: empresaEditando.cod,
            razaosocial: empresaEditando.razaosocial,
            operador: empresaEditando.operador,
            cnpj: cnpjLimpo
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_API_BASE}/api/empresas/${empresaEditando.cnpjOriginal}/atualizar/`,
          {
            razaosocial: empresaEditando.razaosocial,
            cnpj: cnpjLimpo
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setMensagem('Empresa atualizada com sucesso!');
      setShowEditModal(false);
      carregarEmpresas();
    } catch (error) {
      console.error('Erro ao atualizar empresa:', error);
      setMensagem(`Erro ao atualizar empresa: ${error.response?.data?.mensagem || error.response?.data?.erro || error.message}`);
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const baixarZip = (endpoint, nomeArquivo, sucessoMsg, erroMsg) => async () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione ao menos uma empresa');
      return;
    }

    setExecutando(true);
    setMensagem('');

    try {
      const token = localStorage.getItem('access');
      const res = await axios.post(
        endpoint,
        { cnpjs: selecionadas, competencia },
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      setMensagem(sucessoMsg);
    } catch (error) {
      if (error.response?.status === 500) {
        setMensagem('A DARF do mês solicitado ainda não está disponível.');
      } else {
        setMensagem(erroMsg + (error.response?.data?.mensagem || error.message));
      }
    }

    setExecutando(false);
  };

  const executar = baixarZip(
    `${process.env.REACT_APP_API_BASE}/api/dctfweb/`,
    'relatorios_dctfweb.zip',
    'Download de DARFs iniciado com sucesso.',
    'Erro ao gerar DARFs: '
  );

  const gerarRecibos = baixarZip(
    `${process.env.REACT_APP_API_BASE}/api/dctfweb/recibos/`,
    'recibos_dctfweb.zip',
    'Recibos baixados com sucesso.',
    'Erro ao gerar recibos: '
  );

  const gerarDeclaracoes = baixarZip(
    `${process.env.REACT_APP_API_BASE}/api/dctfweb/declaracoes/`,
    'declarações_dctfweb.zip',
    'Declarações baixadas com sucesso.',
    'Erro ao gerar declarações: '
  );

  const gerarXmls = baixarZip(
    `${process.env.REACT_APP_API_BASE}/api/dctfweb/xmls/`,
    'xmls_dctfweb.zip',
    'XMLs normais baixados com sucesso.',
    'Erro ao gerar XMLs: '
  );

  const gerarXmlsAssinados = baixarZip(
    `${process.env.REACT_APP_API_BASE}/api/dctfweb/xmls-assinados/`,
    'xmls_assinados_dctfweb.zip',
    'XMLs assinados baixados com sucesso.',
    'Erro ao gerar XMLs assinados: '
  );

  const consultarGuiasAndamento = baixarZip(
    `${process.env.REACT_APP_API_BASE}/api/dctfweb/guias-em-andamento/`,
    'guias_em_andamento.zip',
    'Guias em andamento baixadas com sucesso.',
    'Erro ao consultar guias: '
  );

  return (
    <div className="dctfweb-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-alt header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">Relatórios DCTFWeb</h2>
          </div>
          <button 
            className="btn btn-success" 
            onClick={() => {
              console.log('Botão Nova Empresa clicado');
              setShowModal(true);
            }}
          >
            <i className="fas fa-plus me-1"></i> Nova Empresa
          </button>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        <div className="d-flex gap-2 mb-3">
          <div>
            <label className="form-label fw-bold">Empresas de</label>
            <select
              className="form-select"
              value={fonte}
              onChange={(e) => setFonte(e.target.value)}
            >
              <option value="dp">Departamento Pessoal</option>
              <option value="fiscal">Relatório Fiscal</option>
            </select>
          </div>

          <div>
            <label className="form-label fw-bold">Mês</label>
            <select
              className="form-select"
              value={competencia.split('/')[0]}
              onChange={(e) => {
                const ano = competencia.split('/')[1];
                setCompetencia(`${e.target.value}/${ano}`);
              }}
            >
              <option value="">MM</option>
              {Array.from({ length: 12 }, (_, i) => {
                const mes = String(i + 1).padStart(2, '0');
                return <option key={mes} value={mes}>{mes}</option>;
              })}
            </select>
          </div>

          <div>
            <label className="form-label fw-bold">Ano</label>
            <select
              className="form-select"
              value={competencia.split('/')[1]}
              onChange={(e) => {
                const mes = competencia.split('/')[0];
                setCompetencia(`${mes}/${e.target.value}`);
              }}
            >
              <option value="">AAAA</option>
              {Array.from({ length: 5 }, (_, i) => {
                const ano = 2022 + i;
                return <option key={ano} value={ano}>{ano}</option>;
              })}
            </select>
          </div>
        </div>

        <div className="d-flex flex-wrap justify-content-end gap-2 mb-2">
          <button className="btn btn-primary px-4" onClick={executar} disabled={executando}>
            {executando ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Gerando...</>) : (<><i className="fas fa-file-invoice me-1"></i> Gerar DARF</>)}
          </button>

          <button className="btn btn-primary px-4" onClick={gerarRecibos} disabled={executando}>
            {executando ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Aguarde...</>) : (<><i className="fas fa-file-alt me-1"></i> Gerar Recibo</>)}
          </button>

          <button className="btn btn-primary px-4" onClick={gerarDeclaracoes} disabled={executando}>
            {executando ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Processando...</>) : (<><i className="fas fa-file-invoice me-1"></i> Baixar Declaração</>)}
          </button>

          <button className="btn btn-primary px-4" onClick={gerarXmls} disabled={executando}>
            {executando ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Baixando...</>) : (<><i className="fas fa-file-alt me-1"></i> Baixar XML</>)}
          </button>

          <button className="btn btn-primary px-4" onClick={gerarXmlsAssinados} disabled={executando}>
            {executando ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Assinando...</>) : (<><i className="fas fa-file-alt me-1"></i> Assinar XML</>)}
          </button>

          <button className="btn btn-primary px-4" onClick={consultarGuiasAndamento} disabled={executando}>
            {executando ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Consultando...</>) : '2° Via de DARF'}
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover text-center align-middle fs-6">
            <thead className="table-light">
              <tr>
                <th></th>
                <th>Código</th>
                <th>Razão Social</th>
                <th>Operador</th>
                <th>CNPJ</th>
                <th>Data vencimento</th>
                <th>Valor</th>
                <th>Data Geração</th>
                <th>Ações</th>
              </tr>
              <tr>
                <th></th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, cod: e.target.value }))}
                  />
                </th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, razaosocial: e.target.value }))}
                  />
                </th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, operador: e.target.value }))}
                  />
                </th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, cnpj: e.target.value }))}
                  />
                </th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, data_vencimento: e.target.value }))}
                  />
                </th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, valor: e.target.value }))}
                  />
                </th>
                <th>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filtrar..."
                    onChange={(e) => setFiltros(prev => ({ ...prev, data_geracao: e.target.value }))}
                  />
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {empresas
                .filter(emp =>
                  Object.entries(filtros).every(([campo, valor]) =>
                    !valor || emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
                  )
                )
                .map((empresa) => (
                  <tr key={empresa.cnpj}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selecionadas.includes(empresa.cnpj)}
                        onChange={() => handleCheckbox(empresa.cnpj)}
                      />
                    </td>
                    <td>{empresa.cod}</td>
                    <td className="text-start">{empresa.razaosocial}</td>
                    <td>{empresa.operador}</td>
                    <td>{formatarCNPJ(empresa.cnpj)}</td>
                    <td>{empresa.data_vencimento || '-'}</td>
                    <td>{empresa.valor}</td>
                    <td>{empresa.data_geracao || '-'}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => abrirEdicao(empresa)}
                        title="Editar empresa"
                        aria-label="Editar empresa"
                        disabled={executando}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Modal customizado sem React Bootstrap */}
        {showModal && (
          <div className="modal-custom">
            <div className="modal-overlay" onClick={() => setShowModal(false)}></div>
            <div className="modal-container">
              <div className="modal-header">
                <h5 className="modal-title mb-0 fw-bold">Cadastrar Nova Empresa</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  cadastrarEmpresa();
                }}>
                  <div className="mb-3">
                    <label htmlFor="cod" className="form-label">Código *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="cod"
                      name="cod"
                      value={novaEmpresa.cod}
                      onChange={handleInputChange}
                      placeholder="Ex: 123"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="razaosocial" className="form-label">Razão Social *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="razaosocial"
                      name="razaosocial"
                      value={novaEmpresa.razaosocial}
                      onChange={handleInputChange}
                      placeholder="Nome da empresa"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="operador" className="form-label">Operador</label>
                    <input
                      type="text"
                      className="form-control"
                      id="operador"
                      name="operador"
                      value={novaEmpresa.operador}
                      onChange={handleInputChange}
                      placeholder="Nome do operador"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="cnpj" className="form-label">CNPJ *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="cnpj"
                      name="cnpj"
                      value={novaEmpresa.cnpj}
                      onChange={(e) => {
                        // Permitir apenas números
                        const apenasNumeros = e.target.value.replace(/\D/g, '');
                        setNovaEmpresa(prev => ({
                          ...prev,
                          cnpj: apenasNumeros
                        }));
                      }}
                      placeholder="Digite apenas números"
                      maxLength={14}
                      required
                    />
                    <small className="text-muted d-block mt-1">
                      Digite apenas os 14 números do CNPJ
                    </small>
                  </div>
                  
                  {/* Botão de envio oculto para permitir submissão pelo Enter */}
                  <input type="submit" hidden />
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={cadastrarEmpresa}
                  disabled={cadastrando}
                >
                  {cadastrando ? 'Cadastrando...' : 'Cadastrar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de edição de empresa */}
        {showEditModal && (
          <div className="modal-custom">
            <div className="modal-overlay" onClick={() => setShowEditModal(false)}></div>
            <div className="modal-container">
              <div className="modal-header">
                <h5 className="modal-title mb-0 fw-bold">Editar Empresa</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  salvarEdicao();
                }}>
                  {fonte === 'dp' && (
                    <div className="mb-3">
                      <label htmlFor="edit-cod" className="form-label">Código *</label>
                      <input
                        type="text"
                        className="form-control"
                        id="edit-cod"
                        name="cod"
                        value={empresaEditando.cod}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="edit-razaosocial" className="form-label">Razão Social *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="edit-razaosocial"
                      name="razaosocial"
                      value={empresaEditando.razaosocial}
                      onChange={handleEditInputChange}
                      required
                    />
                  </div>

                  {fonte === 'dp' && (
                    <div className="mb-3">
                      <label htmlFor="edit-operador" className="form-label">Operador</label>
                      <input
                        type="text"
                        className="form-control"
                        id="edit-operador"
                        name="operador"
                        value={empresaEditando.operador}
                        onChange={handleEditInputChange}
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="edit-cnpj" className="form-label">CNPJ *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="edit-cnpj"
                      name="cnpj"
                      value={empresaEditando.cnpj}
                      onChange={(e) => {
                        const apenasNumeros = e.target.value.replace(/\D/g, '');
                        setEmpresaEditando(prev => ({
                          ...prev,
                          cnpj: apenasNumeros
                        }));
                      }}
                      placeholder="Digite apenas números"
                      maxLength={14}
                      required
                    />
                    <small className="text-muted d-block mt-1">
                      Digite apenas os 14 números do CNPJ
                    </small>
                  </div>

                  {/* Botão de envio oculto para permitir submissão pelo Enter */}
                  <input type="submit" hidden />
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={salvarEdicao}
                  disabled={salvandoEdicao}
                >
                  {salvandoEdicao ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dctfweb;